import * as state from '@/components/Analysis/state';
import { AssistantModal, Composer, Thread, ThreadWelcome } from '@assistant-ui/react';
import { useVercelUseChatRuntime } from '@assistant-ui/react-ai-sdk';
import { makeMarkdownText } from '@assistant-ui/react-markdown';
import '@assistant-ui/react-markdown/styles/markdown.css';
import '@assistant-ui/react/styles/index.css';
import '@assistant-ui/react/styles/modal.css';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { useChat } from '@ai-sdk/react';
import { useFeatureFlag, useRouter } from '@/hooks';
import { useCallback, useEffect, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { Maybe, Vector3, useGetStructureNameQuery } from '@/__generated__/graphql';
import { useDrawerOpen } from '@/hooks/useDrawerOpen';
import { useDrawerWidths } from '@/hooks/useDrawerWidths';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { auth0TokenState } from '@/state';
import { abyssColors } from '@/theme/colors';
import { useImageFromLocationId } from '@/components/Analysis/Viewer/hooks/useImageFromLocationId';
import { useAuth0 } from '@auth0/auth0-react';

const excludedKeys = new Set(['circuit', 'line_number', 'equipment_tag']);

export const Assistant = ({ isRightDrawerOpen = true }: { isRightDrawerOpen?: boolean }) => {
  const accessToken = useAtomValue(auth0TokenState);
  const [chatState, setChatState] = useAtom(state.chatState);
  const [chatOpen, setChatOpen] = useAtom(state.chatOpen);
  const selectedAssemblyTemplateId = useAtomValue(state.selectedAssemblyTemplateId);
  const poiTemplates = useAtomValue(state.allPointOfInterestTemplates);
  const allTemplates = useAtomValue(state.allAssemblyTemplates);
  const allViewpoints = useAtomValue(state.structureLocations);
  const setCameraTarget = useSetAtom(state.cameraTarget);
  const setSelectedSpherical = useSetAtom(state.selectedSpherical);
  const getSphericalFromId = useImageFromLocationId(allViewpoints);
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const { rightDrawerWidth } = useDrawerWidths();
  const { user } = useAuth0();

  const drawerOpen = useDrawerOpen('right');
  const router = useRouter();
  const { inspection } = router.query;
  const structureId = useGetStructureId();
  const MarkdownText = makeMarkdownText();

  const { data: structureNameData } = useGetStructureNameQuery({
    variables: { structureId: inspection?.toString() ?? '' },
    fetchPolicy: 'cache-first',
  });

  const poiTemplatesList = useMemo(() => {
    return poiTemplates.map((template) => ({
      name: template.name,
      fields: template.fields.map((field) => ({ name: field.name, id: field.id })),
    }));
  }, [poiTemplates]);

  const assemblyFilterKeys = useMemo(() => {
    return (
      allTemplates
        ?.at(0)
        ?.fields.filter((field) => field.displayInQuery)
        .filter((field) => !excludedKeys.has(field.key ?? ''))
        .map((field) => ({
          name: field.name,
          key: field.key,
          ...(field.options?.length > 0 ? { options: field.options } : {}),
        })) ?? []
    );
  }, [allTemplates]);

  const goToImageView = useCallback(
    ({
      locationId,
      id,
      assemblyTagName,
      centerPoint3d,
      newTab,
    }: {
      locationId?: string;
      id?: string;
      assemblyTagName?: string;
      centerPoint3d?: Maybe<Vector3>;
      newTab?: boolean;
    }) => {
      if (!locationId) return;
      const spherical = getSphericalFromId(locationId);
      if (!spherical) return;
      const { position, name: sphericalName } = spherical ?? {};
      if (!spherical || !sphericalName) return;

      const { x, y, z } = centerPoint3d || {};
      if (x === undefined || y === undefined || z === undefined) return;

      const lookAt = { x, y, z };
      setCameraTarget({ position, lookAt: [x, y, z] });
      setSelectedSpherical({ ...spherical, lookAt });

      let url = `/analysis/${router.query.inspection}/viewer`;

      if (newTab) {
        const lookAtParameters = `&look.x=${x}&look.y=${y}&look.z=${z}`;
        url += `?assembly=${assemblyTagName}&poi=${id}${lookAtParameters}&selectedSpherical=${sphericalName}`;
        window.open(url, '_blank');
      } else {
        router.push(url);
      }
    },
    [getSphericalFromId, setCameraTarget, setSelectedSpherical, router]
  );

  const navigate = ({ pageTitle }: { pageTitle: string }) => {
    const navbarPages = new Set(['insights', 'viewer', 'deck plan', 'reports']);
    if (navbarPages.has(pageTitle.toLowerCase())) {
      router.push(`/analysis/${structureId}/${pageTitle.toLowerCase().replaceAll(' ', '')}/`);
      return;
    }
  };

  const viewAssembly = ({ tagName, newTab }: { tagName: string; newTab: boolean }) => {
    const url = `/analysis/${structureId}/viewer?assembly=${tagName}`;
    if (newTab) window.open(url, '_blank');
    else router.push(url);
  };

  const chat = useChat({
    api: new URL('/chat', import.meta.env.VITE_APP_API_URL).href,
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    maxSteps: 5,
    body: {
      token: accessToken,
      structure: {
        id: structureId,
        name: structureNameData?.structure?.name ?? 'N/A',
      },
      assemblyTemplateId: selectedAssemblyTemplateId,
      assemblyFilterKeys,
      poiTemplatesList,
      url: window.location.href,
      userName: user?.name,
    },

    // run client-side tools that are automatically executed:
    async onToolCall({ toolCall }) {
      if (toolCall.toolName === 'navigate') {
        const toolArguments = toolCall.args as { pageTitle?: string };
        navigate({ pageTitle: toolArguments?.pageTitle ?? '' });
      }
      if (toolCall.toolName === 'viewAssemblyPointCloud') {
        const toolArguments = toolCall.args as { tagName?: string; newTab?: boolean };
        viewAssembly({
          tagName: toolArguments?.tagName ?? '',
          newTab: toolArguments.newTab ?? false,
        });
      }
      if (toolCall.toolName === 'viewReport') {
        const toolArguments = toolCall.args as { id?: string };
        setSnackbarMessage({
          shouldShow: true,
          content: <Alert severity="success">Report has been created successfully</Alert>,
        });
        setTimeout(() => {
          const url = `/analysis/${structureId}/reports/${toolArguments.id ?? ''}`;
          setSnackbarMessage({
            shouldShow: false,
            content: undefined,
          });
          window.open(url, '_blank');
        }, 1000);
      }
      if (toolCall.toolName === 'viewPointOfInterestIn3D') {
        const toolArguments = toolCall.args as {
          locationId?: string | undefined;
          id?: string | undefined;
          assemblyTagName?: string | undefined;
          centerPoint3d?: Maybe<Vector3> | undefined;
          newTab?: boolean | undefined;
        };
        if (
          (toolArguments.assemblyTagName,
          toolArguments.locationId,
          toolArguments.centerPoint3d,
          toolArguments.id)
        ) {
          goToImageView({ ...toolArguments });
        }
      }
    },
  });

  useEffect(() => {
    if (chatState.length > 0 && chat.messages.length === 0) {
      chat.setMessages(chatState);
    }
  }, [chat, chatState]);

  useEffect(() => {
    setChatState(chat.messages);
  });

  const runtime = useVercelUseChatRuntime(chat);

  const config = {
    runtime,
    assistantMessage: { components: { Text: MarkdownText } },
    assistantAvatar: { src: '/assets/kraken.png', alt: 'logo' },
    strings: {
      assistantModal: {
        open: {
          button: {
            tooltip: 'Kraken - Fabric AI',
          },
        },
        closed: {
          button: {
            tooltip: 'Kraken - Fabric AI',
          },
        },
      },
    },
  };

  const chatOpenHandler = () => {
    setChatOpen((previous) => !previous);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: '2rem',
        right: drawerOpen && isRightDrawerOpen ? `calc(${rightDrawerWidth}px + 2rem )` : '2rem',
        zIndex: '2000 !important',
        '& .aui-modal-anchor': {
          position: 'relative !important',
        },
        '& .aui-modal-button': {
          width: '4rem',
          height: '4rem',
          background: abyssColors.primary[700],
        },
      }}
    >
      <AssistantModal.Root config={config} defaultOpen={chatOpen}>
        <AssistantModal.Trigger onClick={chatOpenHandler}>
          <img style={{ width: '2.4rem', height: '2.4rem' }} src="/assets/kraken.png" />
        </AssistantModal.Trigger>
        <AssistantModal.Content>
          <Thread.Root>
            <Box
              sx={{
                height: '35px',
                padding: '8px',
              }}
            >
              <Typography
                sx={{
                  padding: '1px 8px',
                  background: abyssColors.primary[400],
                  borderRadius: '10px',
                  width: 'fit-content',
                  height: 'fit-content',
                  fontSize: '11px',
                  color: 'white',
                }}
              >
                Beta
              </Typography>
            </Box>
            <Thread.Viewport>
              <ThreadWelcome.Root>
                <ThreadWelcome.Center>
                  <Stack alignItems="center" gap="2.4rem">
                    <ThreadWelcome.Avatar />
                    <Typography>
                      Welcome to <b>Abyss Fabric</b>! 🌊
                    </Typography>
                    <Typography fontSize="1.2rem">
                      Hi, I’m <b>Kraken</b>, your intelligent assistant here to help you navigate
                      the depths of {structureNameData?.structure?.name ?? 'Abyss'}.
                    </Typography>
                    <Typography fontSize="1.2rem">
                      Need assistance? Just type "Help". Let’s dive in! 🐙
                    </Typography>
                  </Stack>
                </ThreadWelcome.Center>
                <ThreadWelcome.Suggestions />
              </ThreadWelcome.Root>
              <Thread.Messages />
              <Thread.FollowupSuggestions />
              <Thread.ViewportFooter>
                <Thread.ScrollToBottom />
                <Composer />
              </Thread.ViewportFooter>
            </Thread.Viewport>
          </Thread.Root>
        </AssistantModal.Content>
      </AssistantModal.Root>
    </Box>
  );
};

export const EchoChat = ({ isRightDrawerOpen = true }: { isRightDrawerOpen?: boolean }) => {
  const isPilotTester = useFeatureFlag('pilot-tester');

  if (!isPilotTester) return <></>;

  return <Assistant isRightDrawerOpen={isRightDrawerOpen} />;
};
